/**
 * The booth station, the shapes and small helpers the booth's screens share (split out of BoothStation.tsx
 * by interaction — James, Oct 21, item 5; no behavior change).
 */
import { type ReactNode } from 'react';
import { formatTime } from '@cider/ui';

export interface Shift {
  shiftId: string;
  area: string;
  title: string;
  startsAt: string;
  endsAt: string;
  capacity: number;
  expected: number;
  checkedIn: number;
  short: number;
  /** The area's minimum age, where it sets one (state 17). */
  minimumAge?: number | null;
  /** Whether the area's volunteers wear a tag (the area sets it; state 11). */
  tagsRequired?: boolean;
}
export interface Today {
  festivalYear: number;
  label: string;
  /** The walk-around unit (state 11): a Tablet station — no printer, no issue. */
  walkAround?: boolean;
  day: string;
  practice: boolean;
  /** A look, not a day (James, Oct 6): nothing is written here, and the sentence says why. */
  lookOnly?: boolean;
  lookSays?: string | null;
  demo?: boolean;
  checkedIn: number;
  checkedOut: number;
  lastIn: {
    at: string;
    personId: string;
    shiftId: string;
    name: string;
    area: string;
    startsAt: string;
    endsAt: string;
  }[];
  shifts: Shift[];
  says: string;
}
export interface Found {
  personId: string;
  name: string;
  today: {
    shiftId: string;
    signupId: string | null;
    area: string;
    title: string;
    startsAt: string;
    endsAt: string;
    state: 'expected' | 'in' | 'out';
    /** Somebody else is working it (7b): their name, and no Check in. */
    workedBy: string | null;
    /** When they were checked in to it (state 16). */
    inAt?: string | null;
    /** The area's minimum age (state 17). */
    minimumAge?: number | null;
    /** Whether the area's volunteers wear a tag (state 11). */
    tagsRequired?: boolean;
  }[];
  /** The quiet flag (7b): "see the booth chair" in place of Check in. */
  quiet: boolean;
  /** An emergency contact on the record (James, Oct 8): asked of all, required under 18. */
  emergency?: boolean;
  /** The year's waiver still to sign, from the find. */
  needsWaiver?: boolean;
  /** Age from the year and month of birth (Oct 8); null where nobody has said. */
  age?: number | null;
  /** "Under 18?" has an answer. */
  ageKnown?: boolean;
  /** Why are you here (Rebuild 29), asked once a year. */
  whyHere?: { reason: string; serviceGroup: string | null; on: string } | null;
  /** Earlier this year, not credited to a family. */
  earlier?: { checkInId: string; area: string; day: string; hours: string }[];
  /** The card's household line (Oct 6). */
  household?: string | null;
  /** The card's other lines (ON11 item 2). */
  phone?: string | null;
  minor?: boolean;
  credentials?: string[];
  /** The family question's answer this year, or null when nobody asked yet. */
  creditAnswered?: string | null;
  /** A chair (ruling 2): the areas they chair — they are not checked in at the booth. */
  chairOf?: string[];
  /** Issued at the booth (7e): drawn only for a chair; what they hold now. */
  issue: { eligible: boolean; out: { id: string; itemName: string; number: string }[] };
  otherDays: { day: string; area: string; startsAt: string; endsAt: string }[];
}
export interface Tag {
  name: string;
  area: string;
  hours: string;
  day: string;
  /** The tag's own code (7c): its QR, scanned at any station to check out. */
  code?: string | undefined;
  /** The ISSUED line (7d): a chair's radio and keys; empty for everyone else. */
  issued?: string | undefined;
  /** A group's name, small under the area (the drawing's tag). */
  group?: string | undefined;
}

/** What a tag's QR carries — a scanner types it into the station's box (7c). */
/** The drawing's lucide marks (ISC): scan-line in the search box, user-plus on New person. */
export function ScanIcon(): ReactNode {
  return (
    <svg
      className="booth__searchicon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 12h10" />
    </svg>
  );
}
export function PersonPlusIcon(): ReactNode {
  return (
    <svg
      className="booth__btnicon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" x2="19" y1="8" y2="14" />
      <line x1="22" x2="16" y1="11" y2="11" />
    </svg>
  );
}

/** Why a result matched (Rebuild 29): today · a mobile ending · under another name. */
export function matchedBy(p: Found, q: string): string {
  const digits = q.replace(/\D/g, '');
  if (digits.length >= 4 && (p.phone ?? '').replace(/\D/g, '').includes(digits))
    return `mobile ending ${(p.phone ?? '').replace(/\D/g, '').slice(-4)}`;
  const words = q
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .trim();
  if (words && !p.name.toLowerCase().includes(words)) return 'under another name';
  return p.today.length ? 'today' : '';
}
/** Results stay put while typing: today's shifts first, then by name — never by the server's whim. */
export function stable(rows: Found[]): Found[] {
  return [...rows].sort(
    (a, b) =>
      Number(b.today.length > 0) - Number(a.today.length > 0) || a.name.localeCompare(b.name),
  );
}

export const SCAN = /^CIDER-T:/i;
/** A check-in to its check-out, as the check-out says it: "4 h 4 m". */
export function spanOf(inAt: string, outAt: string): string {
  const m = Math.max(
    0,
    Math.round((new Date(outAt).getTime() - new Date(inAt).getTime()) / 60_000),
  );
  return m >= 60 ? `${String(Math.floor(m / 60))} h ${String(m % 60)} m` : `${String(m)} m`;
}

/** How long since a check-in, as the check-out says it: "3 h 55 m". */
export function worked(inAt: string): string {
  const m = Math.max(0, Math.round((Date.now() - new Date(inAt).getTime()) / 60_000));
  return m >= 60 ? `${String(Math.floor(m / 60))} h ${String(m % 60)} m` : `${String(m)} m`;
}

/** What a group leader's QR carries — the group card (ON10 item 4). */
export const GROUP = /^CIDER-G:/i;
/** The family page's QR (Rebuild 29, state 6): the household card. */
export const HOUSE = /^CIDER-H:/i;

export const hours = (a: string, b: string): string => `${formatTime(a)} – ${formatTime(b)}`;
export const first = (name: string): string => name.split(' ')[0] ?? name;

export const late = (m: number): string =>
  m >= 60 ? `${String(Math.floor(m / 60))} h ${String(m % 60)} m late` : `${String(m)} m late`;
export const stat = (n: string, label: string, tone?: 'ok' | 'warn'): ReactNode => (
  <div className={`booth__stat${tone ? ` booth__stat--${tone}` : ''}`} key={label}>
    <b>{n}</b>
    <span>{label}</span>
  </div>
);

/** What a tag carries and nothing else (state 39), as drawn. */
export const ON_THE_TAG: [string, string, string][] = [
  ['First name, large', '', 'Readable across a tent.'],
  ['Area · shift time', '', ''],
  ['The ISSUED line', 'chairs and the directors’ list only', 'Empty for everyone else.'],
  ['A group’s name', 'small, under the area', ''],
  [
    'A small QR',
    'the check-out',
    'Scanned at any station it checks the person out. An undone check-in stops the tag working.',
  ],
];
